import React, { useCallback, useEffect, useState } from 'react';

import { api } from 'services/api';
import { isAxiosError } from 'services/axios';
import { shouldTreatSuggestedAsCompositionOriginal } from 'shared/budget-component-type';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  useMediaQuery,
  ModalFooter,
  HStack,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import OutdatedComponentsTable from './ComponentsTable';
import { FlattenedComponent } from './types';

interface ModalBudgetCustomBDIProps extends ModalRootProps {
  data: {
    budgetId: number;
  };
}

type BDIPayload = {
  [componentId: number]: number | null;
};

const modalMargin = 120;
const modalMobileFix = 100;

const ModalBudgetCustomBDI: React.FC<ModalBudgetCustomBDIProps> = ({
  data: { budgetId },
  handleClose,
  onConfirm,
  ...restProps
}) => {
  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const [saving, setSaving] = useState(false);

  const [components, setComponents] = useState<FlattenedComponent[]>([]);

  const [loading, setLoading] = useState(false);

  const getData = useCallback(async () => {
    setLoading(true);
    setComponents([]);

    try {
      const { data: response } = await api.get(
        `/budget/${budgetId}/component`,
        {
          params: {
            // sort: 'compositions.sorteable',
          },
        },
      );

      const componentsData = response.data;

      setComponents(componentsData);
    } catch (err) {
      toast({
        status: 'error',
        description:
          err.response?.data?.message ||
          'Houve um erro ao buscar os itens do orçamento',
      });

      setComponents([]);
    } finally {
      setLoading(false);
    }
  }, [budgetId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleUpdateComponentBDI = (
    id: number,
    bdi: number | undefined,
  ): void => {
    const component = components.find((c) => c.id === id);

    if (!component) return;

    const allComponentsWithSameCode = components.filter((c) => {
      if (
        shouldTreatSuggestedAsCompositionOriginal({
          type: c.type,
          total: c.total,
        })
      ) {
        return (
          shouldTreatSuggestedAsCompositionOriginal({
            type: component.type,
            total: component.total,
          }) &&
          c.composition_price?.composition.code ===
            component.composition_price?.composition.code
        );
      }

      if (c.type === 'input_original') {
        return (
          c.type === component.type &&
          c.input_price?.input.code === component.input_price?.input.code
        );
      }

      if (c.type === 'composition_custom') {
        return (
          c.type === component.type &&
          c.budget_composition_price?.code ===
            component.budget_composition_price?.code
        );
      }

      return false;
    });

    allComponentsWithSameCode.forEach((c) => {
      const index = components.findIndex((comp) => comp.id === c.id);
      const updatedComponent = { ...components[index], bdi_percent: bdi };

      setComponents((prev) => [
        ...prev.slice(0, index),
        updatedComponent,
        ...prev.slice(index + 1),
      ]);
    });
  };

  const handleSubmitComponents = useCallback(async () => {
    const hasNegativeBDI = components.some(
      (component) =>
        component.bdi_percent !== null &&
        component.bdi_percent !== undefined &&
        component.bdi_percent < 0,
    );

    if (hasNegativeBDI) {
      toast({
        description: `O BDI não pode ser negativo. Verifique os valores informados.`,
        status: 'error',
      });

      return;
    }

    try {
      setSaving(true);

      const payload: BDIPayload = components
        .filter((component) => {
          return (
            component.type === 'input_original' ||
            shouldTreatSuggestedAsCompositionOriginal({
              type: component.type,
              total: component.total,
            }) ||
            component.type === 'composition_custom'
          );
        })
        .reduce((acc, component) => {
          const bdi = (() => {
            if (
              component.bdi_percent !== null &&
              component.bdi_percent !== undefined
            ) {
              return component.bdi_percent;
            }

            return null;
          })();

          acc[component.id] = bdi;
          return acc;
        }, {} as BDIPayload);

      await api.put(`/budget/${budgetId}/component/bdi`, {
        budget_components: payload,
      });

      toast({
        description: `Solicitação de atualização de BDI em massa enviada com sucesso. Você será notificado quando a atualização for concluída.`,
        status: 'success',
      });

      if (onConfirm) onConfirm();
      handleClose();
    } catch (err) {
      if (isAxiosError(err)) {
        toast({
          description:
            err.response?.data?.message ||
            'Houve um erro ao enviar a solicitação de atualização de BDI',
          status: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  }, [handleClose, onConfirm, budgetId, components]);

  return (
    <Modal {...restProps} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>Configurar BDI Diferenciado em massa</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <OutdatedComponentsTable
            data={components}
            loading={loading}
            onUpdateComponentBDI={handleUpdateComponentBDI}
          />
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              colorScheme="green"
              onClick={handleSubmitComponents}
              isLoading={saving}
              isDisabled={loading || saving}
            >
              Confirmar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetCustomBDI;
